import { isString } from "generic-type-guard";
import { cloneDeep } from "lodash";
import { call, put, SagaGenerator, select } from "typed-redux-saga";

import { ModelPath } from "@com.mgmtp.a12.base/base-model-api/lib/main/model";
import { Activity, ActivityActions, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import {
	EngineStore,
	ModelSelectors,
	UiStateSelectors
} from "@com.mgmtp.a12.formengine/formengine-core/lib/back-end/store";
import { Commands } from "@com.mgmtp.a12.formengine/formengine-core/lib/back-end/store/internal/actions";
import {
	FormActivity,
	FormEngineActions,
	FormEngineSelectors
} from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions";
import { ReadonlyObjectMap } from "@com.mgmtp.a12.formengine/formengine-core/lib/models/internal/utils/json";
import { DocumentServiceFactory, EntityInstancePath } from "@com.mgmtp.a12.kernel/kernel-md-facade";

import { isWorkflowActivityWithLoadedData, validateActivity } from "./activityUtils";
import { findAnnotations } from "./modelHelpers";
import {
	convertEntityInstancePathToString,
	entityInstancePathFromString,
	getValueFromModelPath,
	parseToModelPath
} from "./pathHelpers";
import { PATH_TO_VALIDATION_TYP } from "./variables/documentModelPaths";
import { WEAK_VALIDATION_MARKER } from "./variables/modulfRuntimeAnnotations";
import { createEnableValidationStateFormEngineAction, VALIDATION_ACTIONS } from "./workflowUtils";
import { StammdatenFormDescriptor } from "./descriptors";

/**
 * Turns off the validation for a specific document in an activity, executes a given function and turns on the
 * validation again. Currently used to turn off the validation in the "Dokument generieren" module because otherwise
 * the document can't be generated if not all required fields are filled.
 * @param activityId the id of the activity in which the validated data is located.
 * @param func the function to be executed without validation.
 * @param nextValidationState the validation state to set after the validation. If undefined, then the last set validation state will be used.
 */
export function* executeWithoutValidation<T>(
	activityId: string,
	func: () => SagaGenerator<T>,
	nextValidationState?: string
): SagaGenerator<T> {
	return yield* executeWithValidationAction(activityId, func, VALIDATION_ACTIONS.NULL, nextValidationState);
}

export function* executeWithCompleteValidation<T>(
	activityId: string,
	func: () => SagaGenerator<T>,
	nextValidationState?: string
): SagaGenerator<T> {
	return yield* executeWithValidationAction(activityId, func, VALIDATION_ACTIONS.COMPLETE, nextValidationState);
}

export function* executeWithSaveValidation<T>(
	activityId: string,
	func: () => SagaGenerator<T>,
	nextValidationState?: string
): SagaGenerator<T> {
	return yield* executeWithValidationAction(activityId, func, VALIDATION_ACTIONS.SAVE, nextValidationState);
}

/**
 * Führt eine Speicher-Validierung (SAVE-Level) für die angegebene Aktivität durch, wie sie z.B. vor dem
 * regulären Speichern-Button (siehe customSaveSaga) verwendet wird. Pflichtfelder, die erst später im
 * Prozess benötigt werden (z.B. Genehmigung/Ablehnung), müssen dafür noch nicht gefüllt sein - das
 * Dokument muss lediglich speicherbar sein.
 *
 * Setzt den ValidationTyp im Dokument nach der Prüfung explizit auf NULL zurück (statt auf den zuvor
 * aktiven Validierungszustand, z.B. COMPLETE). Das ist wichtig, wenn im Anschluss ein echtes
 * Backend-Persist erfolgt (z.B. saveActivity): Ansonsten würde der Server beim Persistieren mit dem
 * zurückgesetzten ValidationTyp (z.B. COMPLETE) eine vollumfängliche Validierung durchführen und dabei
 * unter Umständen Pflichtfeld-Fehler für Felder werfen, die zu diesem Zeitpunkt im Prozess noch nicht
 * gefüllt sein müssen.
 */
export function* validateActivityForSave(activityId: string): SagaGenerator<boolean> {
	return yield* executeWithSaveValidation<boolean>(
		activityId,
		function* () {
			return yield* call(validateActivity, activityId);
		},
		VALIDATION_ACTIONS.NULL
	);
}

export function* executeWithValidationAction<T>(
	activityId: string,
	wrappedFunction: () => SagaGenerator<T>,
	validationAction: string,
	nextValidationState?: string
): SagaGenerator<T> {
	const activity = yield* select(ActivitySelectors.activityById(activityId));
	if (!activity || !isWorkflowActivityWithLoadedData(activity)) {
		throw new Error("Not a workflow activity!");
	}

	if (StammdatenFormDescriptor.isInstance(activity.descriptor)) {
		return yield* wrappedFunction();
	}

	const data = Activity.findDefaultDataHolder(activity)?.data;
	if (!FormActivity.Data.SingleDocumentData.isInstance(data)) {
		throw new Error("No document data found!");
	}

	const prevValidationState = nextValidationState ?? getValidationState(data);
	yield* put(createEnableValidationStateFormEngineAction(activity.id, validationAction));
	const returnValue = yield* wrappedFunction();
	if (prevValidationState != validationAction) {
		const isDirty = yield* select(ActivitySelectors.dirty(activityId));
		yield* put(
			createEnableValidationStateFormEngineAction(
				activity.id,
				isString(prevValidationState) ? prevValidationState : VALIDATION_ACTIONS.NULL
			)
		);
		yield* put(ActivityActions.setDirty({ activityId, dirty: isDirty }));
	}
	return returnValue;
}

function getValidationState(data: FormActivity.Data.SingleDocumentData) {
	const validationState = getValueFromModelPath(PATH_TO_VALIDATION_TYP, data.document) ?? VALIDATION_ACTIONS.NULL;
	return isString(validationState) ? validationState : VALIDATION_ACTIONS.NULL;
}

export function* weakValidation(activityId: string, includeWeak: string[] = []): SagaGenerator<boolean> {
	const activity = yield* select(ActivitySelectors.activityById(activityId));
	if (!activity) {
		console.error(`Activity ${activityId} not found.`);
		return false;
	}

	const engineState = yield* select(FormEngineSelectors.engineState(activityId));
	if (!engineState) {
		console.error("Couldn't get EngineState");
		return false;
	}
	const documentModel = ModelSelectors.documentModel()(engineState);

	const documentModelSearchService = new DocumentServiceFactory().getDocumentModelSearchService(documentModel);
	const weakValidationElements = findAnnotations(documentModel, WEAK_VALIDATION_MARKER);
	const weakValidationElementPaths = Array.from(weakValidationElements.entries())
		.filter(([_, annotations]) => weakValidationNotDisabled(annotations, includeWeak))
		.map(([path]) => path);

	const weakValidationGroupPaths = weakValidationElementPaths
		.map(e => e.slice(1))
		.filter(e => documentModelSearchService.getByPath(e)?.type === "Group")
		.map(e => ModelPath.toString(e));
	const weakValidationFieldPaths = weakValidationElementPaths
		.map(e => e.slice(1))
		.filter(e => documentModelSearchService.getByPath(e)?.type === "Field")
		.map(e => ModelPath.toString(e));

	const filter_func = (ms: string): boolean => {
		const pathString = ModelPath.toString(parseToModelPath(entityInstancePathFromString(ms)));
		return (
			!weakValidationFieldPaths.some(fieldPath => pathString === fieldPath) &&
			!weakValidationGroupPaths.some(groupPath => pathString.startsWith(groupPath))
		);
	};

	return yield* call(executeWithCompleteValidation<boolean>, activity.id, function* (): SagaGenerator<boolean> {
		const messageState = yield* call(validate, activityId, filter_func);
		return Object.keys(messageState).length === 0;
	});
}

/**
 * Führt eine vollständige Validierung (kein Weak-Filter, siehe {@link weakValidation}) des aktuell
 * sichtbaren Screens der Aktivität durch. Genutzt z.B. beim Signieren und beim Herunterladen des
 * signierten Dokuments, wo im Gegensatz zu weakValidation auch schwach markierte Felder/Gruppen
 * geprüft werden sollen, aber nur auf dem aktuellen Screen (nicht im gesamten Dokument).
 */
export function* screenValidation(activityId: string): SagaGenerator<boolean> {
	const engineState = yield* select(FormEngineSelectors.engineState(activityId));
	if (!engineState) {
		return false;
	}
	const screenPath = UiStateSelectors.currentScreenLocation()(engineState).path;

	return yield* call(executeWithCompleteValidation<boolean>, activityId, function* (): SagaGenerator<boolean> {
		const messageState = yield* call(validate, activityId, () => true, undefined, screenPath);
		return Object.entries(messageState).length === 0;
	});
}

function weakValidationNotDisabled(annotations: Record<string, string>, includeWeak: string[]) {
	return (
		Object.entries(annotations).find(
			([key, value]) => key === WEAK_VALIDATION_MARKER && !includeWeak.includes(value)
		) !== undefined
	);
}

export function* validate(
	activityId: string,
	messageStateFilterFunction: (key: string) => boolean,
	oldMessageState?: ReadonlyObjectMap<EngineStore.Validation.Entry>,
	screenPath?: EntityInstancePath
): SagaGenerator<ReadonlyObjectMap<EngineStore.Validation.Entry>> {
	oldMessageState = oldMessageState ?? cloneDeep(yield* call(getMessageState, activityId));

	yield* put(
		FormEngineActions.event({
			activityId,
			engineEvent: Commands.validateFull()
		})
	);

	const updatedMessageState = yield* call(getMessageState, activityId);
	const newMessageState = filterMessageState(updatedMessageState, messageStateFilterFunction, screenPath);

	if (newMessageState !== oldMessageState) {
		yield* put(
			FormEngineActions.command({
				activityId: activityId,
				engineEvent: Commands.setMessageState({
					messages: newMessageState
				})
			})
		);
	}

	return newMessageState;
}

export function* getMessageState(activityId: string): SagaGenerator<ReadonlyObjectMap<EngineStore.Validation.Entry>> {
	const engineState = yield* select(FormEngineSelectors.engineState(activityId));
	return engineState ? engineState.ui.messages : {};
}

export function filterMessageState(
	messageState: ReadonlyObjectMap<EngineStore.Validation.Entry>,
	messageStateFilterFunction: (key: string) => boolean,
	screenPath?: EntityInstancePath
): ReadonlyObjectMap<EngineStore.Validation.Entry> {
	let filteredValues: ReadonlyObjectMap<EngineStore.Validation.Entry> = {};
	const screenPathString = screenPath ? convertEntityInstancePathToString(screenPath) : "";

	for (const [key, entry] of Object.entries(messageState)) {
		if (messageStateFilterFunction(key) && key.startsWith(screenPathString)) {
			filteredValues = {
				...filteredValues,
				[key]: entry
			};
		}
	}

	return filteredValues;
}
